/**
 * VitaAdvisor experiments (PostHog feature flags).
 *
 * Each experiment has:
 *   - key: the PostHog feature flag key the user creates in their dashboard
 *   - default: variant served when PostHog hasn't loaded OR flag isn't created
 *   - variants: a map of variant -> presentation data
 *
 * Pattern: components call useExperiment(EXPERIMENTS.X) and get { variant, content }.
 * The hook auto-fires `experiment_viewed` once per session for clean exposure analytics.
 */

export const EXPERIMENTS = {
  // 1. Landing page headline
  LANDING_HEADLINE: {
    key: "landing-headline",
    default: "control",
    variants: {
      control: {
        eyebrow: "Smart health · real results",
        line1: "Your body,",
        line1_em: "understood.",
        line2: "Your next step,",
        line2_em: "clear.",
        sub: "Feel Greats turns 3 minutes of honest answers into a personalized health report and a matched Unicity Feel Great plan — explained in your language, your tone, and your goals.",
      },
      problem_focused: {
        eyebrow: "If you've tried everything, this is different",
        line1: "Find the",
        line1_em: "real cause",
        line2: "behind your",
        line2_em: "health struggles.",
        sub: "Stop guessing. In 3 minutes you'll see what's actually driving your weight, energy and hormone issues — plus the exact next step to fix it.",
      },
      outcome_focused: {
        eyebrow: "Built for people who are done waiting",
        line1: "Better energy.",
        line1_em: "Clearer skin.",
        line2: "A body that",
        line2_em: "feels good again.",
        sub: "Take the 3-minute health assessment and walk away with a personalised, doctor-informed plan from the Unicity Feel Great System — backed by 250k+ real transformations.",
      },
    },
  },

  // 2. Hero CTA button text
  LANDING_CTA: {
    key: "landing-cta",
    default: "control",
    variants: {
      control: { text: "Start my free assessment" },
      urgent: { text: "Show me my results — 3 min" },
      personal: { text: "Find my real problem" },
    },
  },

  // 3. Subscription banner emphasis on ProductCard
  SUBSCRIBE_BANNER: {
    key: "subscribe-banner-emphasis",
    default: "control",
    variants: {
      control: {
        showBadge: true,
        badgeText: "Most recommended",
        accent: "strong", // visual emphasis: strong | soft | none
        copy:
          "Better results (consistent daily routine) · 10% off every month · Pause or cancel anytime",
      },
      softer: {
        showBadge: true,
        badgeText: "Best value",
        accent: "soft",
        copy: "Save 10% · pause or cancel anytime",
      },
      hidden: {
        showBadge: false,
        badgeText: "",
        accent: "none",
        copy: "Save 10% with monthly auto-ship · cancel anytime",
      },
    },
  },

  // 4. Result page urgency tone
  RESULT_URGENCY_TONE: {
    key: "result-urgency-tone",
    default: "mild",
    variants: {
      mild: { tone: "mild" }, // current copy from backend
      strong: { tone: "strong", prefix: "⚠️ Don't wait — " },
      none: { tone: "none" }, // hide the "if unaddressed" block
    },
  },

  // 5. WhatsApp message style — sent as ?style= to /api/whatsapp/message
  WHATSAPP_STYLE: {
    key: "whatsapp-message-style",
    default: "default",
    variants: {
      default: { style: "default" },
      punchy: { style: "punchy" },
      story: { style: "story" },
    },
  },
};
